import { useEffect } from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { VisuallyHidden as RadixVisuallyHidden } from '@radix-ui/react-visually-hidden';
import SbUtils from "../../../../Utils/SbUtils";
import { LikesIcon, ViewsIcon, TikTokIcon, PreviousIcon, NextIcon, CloseIcon } from "../../../../Utils/icons";
// perf(SMASH-1836): import from a11y/Dialog, not the a11y barrel — the barrel
// drags the admin-only Radix stack (accordion/tabs) into the public feed bundle.
import {
    DialogRoot,
    DialogPortal,
    DialogOverlay,
    DialogContent,
    DialogTitle,
    DialogClose,
} from '../../../../a11y/Dialog';
import VideoComponent from './Video';
import { useGdpr } from '../../../../Frontend/Hooks/GdprContext';

import './Modal.css';

/**
 * LightboxModal — accessible TikTok post lightbox.
 *
 * Refactored to compose the lower-level Radix Dialog primitives directly
 * (DialogRoot/Portal/Overlay/Content/Title/Close) so existing `sbt-*` class
 * names are preserved while gaining role="dialog", aria-modal, focus trap,
 * focus restore on close, Escape-to-close, and click-outside dismissal for
 * free. Previously the dialog was a bare `<ModalContainer>` whose inner
 * close/prev/next affordances were unfocusable `<div onClick>` elements.
 *
 * Closes findings: v3 C1, C2, C3, C4, C5.
 */
const LightboxModal = ({
    closeModal,
    post,
    feedSettings,
    headerData,
    handleNext,
    handlePrev,
    showNext,
    showPrev,
    isPro,
    isLightboxVisible,
    setIsLightboxVisible,
}) => {
    const { gdprActive, gdprConsented, gdprAvatarPlaceholder } = useGdpr();
    const canLoadExternal = !gdprActive || gdprConsented;

    let captionDescription = post?.video_description || '';
    // if multiple spaces are present in the caption, replace them with a break
    if (captionDescription.includes('  ')) {
        captionDescription = captionDescription.replace(/ {2}/g, '<br /><br />');
    }
    // get the list of hashtags starting with # and add a links to them
    captionDescription = captionDescription.replace(/#(\w+)/g, '<a href="https://www.tiktok.com/tag/$1" target="_blank" rel="noopener noreferrer">#$1</a>');

    let modalSize = isPro ? 'lightbox' : 'lightbox-free';
    modalSize = isPro && post?.video_url ? 'lightbox-pro' : modalSize;

    if (headerData?.length > 1) {
        headerData = headerData.filter(header => header.open_id === post.open_id);
    }
    headerData = headerData[0] || headerData;

    useEffect(() => {
        if (isLightboxVisible) {
            document.body.classList.add('sbt-lightbox-modal-open');
        } else {
            document.body.classList.remove('sbt-lightbox-modal-open');
        }

        window.sbttCurrentLightboxPostID = post?.id;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLightboxVisible])

    const handleOpenChange = (open) => {
        if (!open && typeof closeModal === 'function') {
            closeModal();
        }
    };

    const accessibleTitle = post?.video_description || __('TikTok post', 'sb-customizer');

    return (
        <DialogRoot open={true} onOpenChange={handleOpenChange}>
            <DialogPortal>
                <DialogOverlay
                    className="sb-modal-ctn sbt-lightbox-modal"
                    data-size={modalSize}
                    data-type={feedSettings?.feedTemplate}
                >
                    <DialogContent
                        className="sb-modal-insider"
                        tabIndex={-1}
                        // Explicit aria-modal: the rendered dialog was missing it
                        // (so AT wasn't told the rest of the page is inert).
                        // SMASH-1382 v3 frontend (T2).
                        aria-modal="true"
                        onOpenAutoFocus={(event) => {
                            // Preserve legacy behaviour: do not drop focus
                            // into the first focusable child (often an
                            // input). But we MUST move focus into the
                            // dialog itself, otherwise Radix's focus trap
                            // never engages and Tab cycles through the
                            // underlying page. Focusing the Content
                            // container (tabIndex=-1) keeps focus inside
                            // the dialog so Tab moves to its first
                            // interactive descendant. SMASH-1377 v3-r4.
                            event.preventDefault();
                            event.currentTarget.focus();
                        }}
                    >
                        {/*
                          * Radix wires DialogContent's aria-labelledby to the
                          * DialogTitle automatically via its internal titleId.
                          * Wrapping in Radix's own VisuallyHidden (asChild)
                          * keeps the title in the a11y tree + DOM (so Radix's
                          * runtime "DialogContent requires a DialogTitle" check
                          * passes) while remaining invisible to sighted users.
                          * Using the project sb-visually-hidden class with a
                          * custom id previously overrode Radix's titleId, which
                          * tripped the false-positive console warning.
                          * SMASH-1377 v3-r12.
                          */}
                        <RadixVisuallyHidden asChild>
                            <DialogTitle>{accessibleTitle}</DialogTitle>
                        </RadixVisuallyHidden>

                        <div className="sbt-lightbox-modal-wrapper">
                            <DialogClose asChild>
                                <button
                                    type="button"
                                    className="sbt-lightbox-modal-close sb-button-reset"
                                    aria-label={__('Close', 'sb-customizer')}
                                >
                                    <CloseIcon />
                                </button>
                            </DialogClose>

                            <div className="sbt-lightbox-modal-iframe-wrap">
                                {canLoadExternal && isPro && post?.video_url && <VideoComponent post={post} />}
                                {canLoadExternal && (!isPro || !post?.video_url) &&
                                    <iframe
                                        src={`https://www.tiktok.com/embed/v2/${post?.id}`}
                                        title={post?.video_description || __('TikTok video', 'sb-customizer')}
                                        height={'758px'}
                                        width={'325px'}
                                        style={{
                                            border: 'none',
                                            visibility: 'unset',
                                            margin: '0 auto',
                                        }}
                                    />
                                }
                                {!canLoadExternal &&
                                    <div className="sbt-lightbox-modal-gdpr-notice">
                                        <a href={post?.share_url} target="_blank" rel="noopener noreferrer">
                                            {post?.video_description || __('View on TikTok', 'sb-customizer')}
                                        </a>
                                    </div>
                                }
                            </div>

                            {isPro &&
                                <div className='sbt-lightbox-modal-content'>
                                    <a className='sbt-lightbox-modal-header'
                                        href={`https://www.tiktok.com/@${headerData?.username}`}
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        <img className='sbt-lightbox-modal-avatar'
                                            src={canLoadExternal
                                                ? (headerData?.local_avatar_url || headerData?.avatar_url_100)
                                                : (headerData?.local_avatar_url || gdprAvatarPlaceholder || '')
                                            }
                                            // a11y(SMASH-1377 F8): bio_description-first could announce a
                                            // long unrelated bio for the avatar, duplicating the adjacent
                                            // @username link text. Match the Author.js/Header.js '%s avatar'
                                            // convention; empty (decorative) when there is no username.
                                            alt={headerData?.username
                                                ? sprintf(
                                                    /* translators: %s: account username */
                                                    __('%s avatar', 'sb-customizer'),
                                                    headerData.username
                                                )
                                                : ''
                                            }
                                        />

                                        <div>
                                            <p className='sbt-lightbox-modal-username'>
                                                @{headerData?.username}
                                            </p>
                                            <p className='sbt-lightbox-modal-date'>
                                                {SbUtils.printDate(post?.create_time, feedSettings)}
                                            </p>
                                        </div>
                                    </a>

                                    <div className='sbt-lightbox-modal-caption'
                                        dangerouslySetInnerHTML={{
                                            __html: SbUtils.printText(captionDescription, feedSettings, true)
                                        }}
                                    >
                                    </div>

                                    <div className='sbt-lightbox-modal-footer'>
                                        <div className='sbt-lightbox-modal-stats'>
                                            {/* a11y(SMASH-1377 F6): icon meaning was
                                              * visual-only — SR users heard two bare
                                              * numbers (WCAG 1.1.1). */}
                                            <span className='sbt-lightbox-modal-views'>
                                                <ViewsIcon fillColor={'black'} />
                                                {post?.view_count}
                                                <RadixVisuallyHidden>{__('views', 'sb-customizer')}</RadixVisuallyHidden>
                                            </span>

                                            <span className='sbt-lightbox-modal-likes'>
                                                <LikesIcon fillColor={'black'} />
                                                {post?.like_count}
                                                <RadixVisuallyHidden>{__('likes', 'sb-customizer')}</RadixVisuallyHidden>
                                            </span>
                                        </div>

                                        <a
                                            href={post?.share_url}
                                            target='_blank'
                                            rel='noreferrer'
                                            aria-label={__('View on TikTok', 'sb-customizer')}
                                        >
                                            <TikTokIcon />
                                        </a>
                                    </div>
                                </div>
                            }

                            {showPrev &&
                                <button
                                    type="button"
                                    onClick={handlePrev}
                                    className="sbt-lightbox-modal-prev sb-button-reset"
                                    aria-label={__('Previous post', 'sb-customizer')}
                                >
                                    <PreviousIcon />
                                </button>
                            }

                            {showNext &&
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="sbt-lightbox-modal-next sb-button-reset"
                                    aria-label={__('Next post', 'sb-customizer')}
                                >
                                    <NextIcon />
                                </button>
                            }
                        </div>
                    </DialogContent>
                </DialogOverlay>
            </DialogPortal>
        </DialogRoot>
    );
}

export default LightboxModal;
